import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectCurrentLanguage, updateCurrentLanguage } from "../home/homeSlice";
import { LANGUAGE_CONFIG } from "../assets/i18n";
import type { SupportedLocales } from "../home/homeSlice";

/**
 * 自定义语言管理钩子
 * 提供统一的语言切换、状态同步和错误处理
 */
export const useLanguage = () => {
  const { i18n, ready } = useTranslation();
  const dispatch = useAppDispatch();
  const currentLanguage = useAppSelector(selectCurrentLanguage);

  /**
   * 切换语言
   */
  const changeLanguage = useCallback(async (languageCode: string) => {
    try {
      // 验证语言代码是否支持
      if (!LANGUAGE_CONFIG.SUPPORTED_LANGUAGES.includes(languageCode)) {
        console.warn(`不支持的语言代码: ${languageCode}`);
        return false;
      }

      // 更新i18n语言
      await i18n.changeLanguage(languageCode);
      
      // 更新Redux状态
      const reduxLanguage = LANGUAGE_CONFIG.LANGUAGE_MAP[languageCode as keyof typeof LANGUAGE_CONFIG.LANGUAGE_MAP] as SupportedLocales;
      dispatch(updateCurrentLanguage(reduxLanguage));
      
      return true;
    } catch (error) {
      console.error(`语言切换失败 (${languageCode}):`, error);
      return false;
    }
  }, [i18n, dispatch]);

  /**
   * 获取当前语言信息
   */
  const getCurrentLanguageInfo = useCallback(() => {
    const i18nLanguage = i18n.language;
    const reduxLanguage = currentLanguage;
    
    return {
      i18nLanguage,
      reduxLanguage,
      isReady: ready,
      isSupported: LANGUAGE_CONFIG.SUPPORTED_LANGUAGES.includes(i18nLanguage),
      displayName: i18nLanguage === "zh-CN" ? "中文" : "English"
    };
  }, [i18n, currentLanguage, ready]);

  /**
   * 获取支持的语言列表
   */
  const getSupportedLanguages = useCallback(() => {
    return LANGUAGE_CONFIG.SUPPORTED_LANGUAGES.map(lang => ({
      code: lang,
      displayName: lang === "zh-CN" ? "中文" : "English",
      reduxCode: LANGUAGE_CONFIG.LANGUAGE_MAP[lang as keyof typeof LANGUAGE_CONFIG.LANGUAGE_MAP]
    }));
  }, []);

  /**
   * 检查语言是否已加载
   */
  const isLanguageLoaded = useCallback((languageCode: string) => {
    return i18n.hasResourceBundle(languageCode, "translation");
  }, [i18n]);

  return {
    // 状态
    currentLanguage,
    i18nLanguage: i18n.language,
    isReady: ready,
    
    // 方法
    changeLanguage,
    getCurrentLanguageInfo,
    getSupportedLanguages,
    isLanguageLoaded,
    
    // 配置
    languageConfig: LANGUAGE_CONFIG
  };
};

export type UseLanguageReturn = ReturnType<typeof useLanguage>;